/**
 * Import a pan.do/ra "Edit" (a sequence of clips with in/out times) into a
 * fabled timeline. Pure, reusable helpers shared by the in-editor track button
 * and the project-creation entry point.
 *
 * A clip's `in`/`out` are in CONCATENATED item time; `concatRangeToPartRange`
 * maps them onto a single part (same behaviour as BinPanel.addToTimeline). A
 * clip whose range spans a part boundary is clamped to the part containing its
 * in-point — splitting across parts is a future enhancement.
 */
import { concatRangeToPartRange } from '../format/partRange';
import type { PandoraVideoSource } from '../format/types';
import type { Archive } from './archives';
import { getEdit, getItem, type PandoraEditClip } from './pandora';

export interface ParsedEditUrl {
  archive: Archive;
  editId: string; // "<user>:<name>"
}

/** A resolved clip ready to hand to the timeline store. */
export interface ResolvedEditClip {
  source: PandoraVideoSource;
  inPoint: number;
  outPoint: number;
}

function hostOf(url: string): string {
  return new URL(url).host.toLowerCase();
}

/**
 * Parse an Edit URL like `https://pad.ma/edits/camp.bot:salimedit/annotations/index`
 * into its archive + edit id. Everything after the first path segment following
 * `/edits/` is view state and ignored. Throws a user-facing Error on a host we
 * don't recognise or a URL that isn't an Edit link.
 */
export function parseEditUrl(url: string, archives: Archive[]): ParsedEditUrl {
  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    throw new Error('That doesn’t look like a URL.');
  }
  const host = parsed.host.toLowerCase();
  const archive = archives.find((a) => {
    try {
      return hostOf(a.site) === host;
    } catch {
      return false;
    }
  });
  if (!archive) {
    throw new Error(`Unknown archive “${host}”. Supported: ${archives.map((a) => a.key).join(', ')}.`);
  }
  const segments = parsed.pathname.split('/').filter(Boolean);
  const editsIdx = segments.indexOf('edits');
  const raw = editsIdx >= 0 ? segments[editsIdx + 1] : undefined;
  if (!raw) {
    throw new Error('That URL has no edit id — expected …/edits/<id>.');
  }
  const editId = decodeURIComponent(raw);
  return { archive, editId };
}

/** Convenience: parse against a possibly-undefined archives list (throws if empty). */
export function parseEditUrlOrThrow(url: string, archives: Archive[] | null): ParsedEditUrl {
  if (!archives || archives.length === 0) throw new Error('Archives not loaded yet — try again.');
  return parseEditUrl(url, archives);
}

/** The name portion of an edit id ("<user>:<name>" → "name"), for default titles. */
export function editName(editId: string): string {
  const colon = editId.indexOf(':');
  return colon >= 0 ? editId.slice(colon + 1) : editId;
}

async function resolveClip(
  archive: Archive,
  clip: PandoraEditClip,
): Promise<ResolvedEditClip | null> {
  if (!clip.item) return null;
  let durations = clip.durations ?? [];
  if (durations.length === 0) {
    // Rare: clip omitted durations — fall back to a single item fetch.
    const item = await getItem(archive.site, clip.item);
    durations = item.durations ?? (item.duration ? [item.duration] : []);
  }
  const { part, inPoint, outPoint } = concatRangeToPartRange(durations, clip.in, clip.out);
  if (!(outPoint > inPoint)) return null;
  const source: PandoraVideoSource = {
    kind: 'pandora-video',
    archive: archive.key,
    item: clip.item,
    part,
    duration: durations[part - 1] ?? clip.out - clip.in,
    title: clip.title || clip.item,
    posterFrame: inPoint,
    url: null,
  };
  return { source, inPoint, outPoint };
}

export interface ResolveEditResult {
  editId: string;
  name: string;
  clips: ResolvedEditClip[];
  skipped: number;
}

/**
 * Fetch an Edit and resolve every clip to a `PandoraVideoSource` + in/out.
 * Clips with no resolvable media are dropped and counted in `skipped`.
 */
export async function resolveEditClips(
  archive: Archive,
  editId: string,
): Promise<ResolveEditResult> {
  const edit = await getEdit(archive.site, editId);
  const resolved = await Promise.all(edit.clips.map((c) => resolveClip(archive, c)));
  const clips = resolved.filter((c): c is ResolvedEditClip => c !== null);
  return {
    editId,
    name: edit.name || editName(editId),
    clips,
    skipped: edit.clips.length - clips.length,
  };
}

/** One-shot: parse a URL and resolve its clips. */
export async function importEditFromUrl(
  url: string,
  archives: Archive[],
): Promise<ResolveEditResult & { archiveKey: string }> {
  const { archive, editId } = parseEditUrl(url, archives);
  const result = await resolveEditClips(archive, editId);
  return { ...result, archiveKey: archive.key };
}
